<template>
  <div>
    <TopBar
      title="Unpauser"
      @toggleDrawer="$emit('toggleDrawer')"
      :isLoading="isLoading"
    />
    <v-main v-if="client != null">
      <OCol class="pa-2">
        <div class="pa-2">
          <v-card>
            <v-toolbar flat dense class="card-bar">
              <v-toolbar-title>
                {{ client.id }} ─ {{ client.vehicleCode || '(s/placa)' }}
              </v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="loadingAround('fetchClient')">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <OHoldToConfirm
                :label="'Pausado: ' + (client.isPaused ? 'SIM' : 'NÃO')"
                class="mb-4"
                block
                @confirmed="toggle('isPaused')"
                :color="client.isPaused ? 'amber' : null"
              />
              <OHoldToConfirm
                :label="'Ignorado: ' + (client.isIgnored ? 'SIM' : 'NÃO')"
                block
                @confirmed="toggle('isIgnored')"
                :color="client.isIgnored ? 'error' : null"
              />
            </v-card-text>
          </v-card>
        </div>

        <div class="pa-2">
          <v-card>
            <v-toolbar flat dense class="card-bar">
              <v-toolbar-title>Filas</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="loadingAround('fetchQueues')">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
            <QueueList :rows="queues" />
            <v-divider />
            <v-card-text>
              <v-switch
                class="my-0"
                label="Fechar trecho aberto"
                v-model="closeOpenTrip"
                single-line
                hide-details
              />
              <v-switch
                class="mb-4"
                label="Ignorar crachá desconhecido"
                v-model="ignoreMissingDriver"
                single-line
                hide-details
              />
              <OHoldToConfirm
                class="mb-4"
                block
                @confirmed="restart"
                label="Restart Worker"
              />
              <OHoldToConfirm block @confirmed="flush" label="Flush Queue" />
            </v-card-text>
          </v-card>
        </div>

        <div class="pa-2">
          <v-card>
            <v-toolbar flat dense class="card-bar">
              <v-toolbar-title>Trechos</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="loadingAround('fetchTrips')">
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
            <TripList :rows="trips" />
          </v-card>
        </div>

        <div class="pa-2">
          <v-card>
            <v-toolbar flat dense class="card-bar">
              <v-toolbar-title>Último HB</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="loadingAround('fetchLastHbFromLastTrip')">
                <v-icon>mdi-road-variant</v-icon>
              </v-btn>
              <v-btn icon @click="loadingAround('fetchLastHbFromReceipts')">
                <v-icon>mdi-receipt</v-icon>
              </v-btn>
            </v-toolbar>
            <HbList :rows="lastHbs" />
          </v-card>
        </div>

        <div class="pa-2">
          <v-card>
            <v-toolbar flat dense class="card-bar">
              <v-toolbar-title>A reenviar</v-toolbar-title>
              <v-spacer />
              <v-btn icon @click="loadingAround('fetchHbsToResend')">
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
            </v-toolbar>
            <ORow fill-x class="pa-4">
              <v-text-field
                v-model="fromHbId"
                prefix="Desde o HB"
                dense
                single-line
                hide-details
              />
              <v-btn icon small @click="add(+1)" v-text="'+1'" />
              <v-btn icon small @click="add(-1)" v-text="'-1'" />
            </ORow>

            <div class="pa-4">
              <OHoldToConfirm
                :disabled="withRawdata < 1"
                :label="`Reenviar ${withRawdata}/${total}`"
                block
                @confirmed="resend"
                color="error"
              />
            </div>

            <HbList :rows="hbsToResend" />
          </v-card>
        </div>
      </OCol>
    </v-main>
  </div>
</template>

<script>
import _ from 'lodash'
import { Queue, Heartbeat, PipeDiags, OrgApi, Client, minMs1 } from '@/models'
import OHoldToConfirm from 'oderon-vue/cpts/OHoldToConfirm'
import TopBar from './TopBar.vue'
import QueueList from './QueueList.vue'
import TripList from './TripList.vue'
import HbList from './HbList.vue'

export default {
  components: { TopBar, QueueList, TripList, HbList, OHoldToConfirm },
  props: {
    clientId: { required: true }
  },
  data() {
    return {
      isLoading: false,
      client: null,
      queues: [],
      trips: [],
      lastHbs: [],
      fromHbId: null,
      hbsToResend: [],
      total: 0,
      withRawdata: 0,
      closeOpenTrip: false,
      ignoreMissingDriver: false
    }
  },
  computed: {
    btnColor() {
      let name = this.qname
      if (name == null) return 'error'
      if (name.includes('dev/')) return 'success'
      if (name.includes('hlg/')) return 'amber darken-1 white--text'
      return 'error'
    }
  },
  mounted() {
    this.reload()
  },
  methods: {
    reload() {
      this.$loading(() => this.fetchData())
    },
    async fetchData() {
      await this.fetchClient()
      await this.fetchQueues()
      await this.fetchTrips()
    },
    async fetchClient() {
      this.client = await Client.findById(this.clientId)
    },
    async fetchQueues() {
      this.queues = await PipeDiags.findFullByClient(this.clientId)
    },
    async fetchTrips() {
      let { organizationId, vehicleId } = this.client

      let data = await new OrgApi(organizationId).get('trips', {
        vehicleId,
        order: 'createdAt desc, id desc',
        offset: 0,
        limit: 5
      })

      this.trips = (data && data.rows) || []
    },
    async fetchLastHbFromReceipts() {
      let cid = this.client.id,
        orgId = this.client.organizationId

      let data = await new OrgApi(orgId).get(
        `clients/${cid}/heartbeats?limit=1`
      )

      let id = _.get(data, '[0].heartbeatId')
      await this.fetchHb(id)
    },

    async fetchLastTrip() {
      let orgId = this.client.organizationId,
        vehicleId = this.client.vehicleId

      let data = await new OrgApi(orgId).get('trips', {
        vehicleId,
        order: 'createdAt desc, id desc',
        limit: 1
      })

      return _.get(data, 'rows[0]')
    },

    async fetchLastHbFromLastTrip() {
      let lastTrip = await this.fetchLastTrip()
      if (lastTrip == null) {
        this.$snack('Não existem trechos', { color: 'amber' })
      } else {
        let lastHbId = lastTrip.lastHeartbeatId
        await this.fetchHb(lastHbId)
      }
    },

    async fetchHb(id) {
      if (id != null) {
        this.fromHbId = id+1
        let hb = await Heartbeat.findById(id)
        this.lastHbs = [hb]
        await this.fetchHbsToResend()
      }
    },
    async fetchHbsToResend() {
      // await this.$delay(1000)
      if (this.client == null) return null

      let clientId = this.client.id
      let fromHeartbeat = this.fromHbId

      let data = await Heartbeat.findAll({
        clientId,
        limit: 300,
        fromHeartbeat,
        order: 'receivedOn ASC, id ASC'
      })

      this.hbsToResend = data.rows
      this.total = data.rows.length
      this.withRawdata = data.rows.filter(
        (r) => r.summary.rawDataLength > 0
      ).length
    },

    // selectQueue(row) {},

    async forceCloseTrip(tripId, isValid = true) {
      let fetcher = new OrgApi(this.client.organizationId)
      await fetcher.post(`trips/${tripId}/force-close`, { isValid })
    },

    async restart() {
      this.$loading(async () => {
        if (this.closeOpenTrip) {
          let lastTrip = await this.fetchLastTrip()
          if (lastTrip == null) {
            this.$snack('Não existem trechos', { color: 'amber' })
            return
          }

          if (!lastTrip.isOngoing) {
            this.$snack('Último trecho não está aberto', { color: 'amber' })
            return
          }

          await this.forceCloseTrip(lastTrip.id)
        }

        await PipeDiags.restartWorker({
          orgCode: this.client.organization.name,
          clientId: this.client.id,
          extraInfo: {
            ignoreMissingDriver: this.ignoreMissingDriver
          }
        })

        this.ignoreMissingDriver = false
        this.closeOpenTrip = false
        await this.fetchData()
        this.$snack('Comando enviado')
      })
    },

    async flush() {
      this.$loading(async () => {
        let qname = this.queues[0].queue.name
        if (qname == null) throw new Error('Missing qname')
        await Queue.flush(qname)
        await this.fetchData()
        this.$snack('Comando enviado')
      })
    },

    loadingAround(method) {
      this.$loading(() => minMs1(this[method]()))
    },

    add(x) {
      this.fromHbId = parseInt(this.fromHbId) + x
    },

    async toggle(prop) {
      this.$loading(
        minMs1(async () => {
          await Client.patch(this.client.id, {
            [prop]: !this.client[prop]
          })
          await this.fetchData()
          this.$snack('Flag alterada')
        })
      )
    }
  }
}
</script>

<style></style>
